import { useCallback, useEffect } from 'react';

import { Button } from '@bambi/component-library/src/components/Button';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import { useAppDispatch } from 'src/app/store/hooks';
import {
  MessageSettings,
  useMessagesMessageSettingsRetrieveQuery,
  useMessagesMessageSettingsUpdateMutation,
} from 'src/common/external/bambi-api/messageSettingsApi';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';

import { MessageSettingsForm } from './MessageSettingsForm';

export function MessageSettingsScreen() {
  const dispatch = useAppDispatch();
  const { data: messageSettings, isLoading } =
    useMessagesMessageSettingsRetrieveQuery();

  const [updateMessageSettings] = useMessagesMessageSettingsUpdateMutation();

  const form = useForm<MessageSettings>({
    defaultValues: {
      pickup_message_mode: 'near-pickup',
      seconds_to_send_message_before_pickup_time: 300,
      trip_acknowledgement_message_mode: 'same-day',
      enable_pickup_message: true,
      enable_trip_acknowledgement_message: true,
      enable_reminder_message: true,
      enable_public_trip_portal_message: true,
      enable_trip_canceled_message: true,
    },
    mode: 'all',
  });

  useEffect(() => {
    if (messageSettings) {
      form.reset({
        ...messageSettings,
      });
    }
  }, [messageSettings, form]);

  const handleSubmit = useCallback<SubmitHandler<MessageSettings>>(
    async (data) => {
      try {
        await updateMessageSettings({
          messageSettings: {
            pickup_message_mode: data.pickup_message_mode,
            seconds_to_send_message_before_pickup_time:
              data.seconds_to_send_message_before_pickup_time,
            trip_acknowledgement_message_mode:
              data.trip_acknowledgement_message_mode,
            enable_pickup_message: data.enable_pickup_message,
            enable_trip_acknowledgement_message:
              data.enable_trip_acknowledgement_message,
            enable_reminder_message: data.enable_reminder_message,
            enable_public_trip_portal_message:
              data.enable_public_trip_portal_message,
            enable_trip_canceled_message: data.enable_trip_canceled_message,
          },
        }).unwrap();
        dispatch(
          show({
            title: 'Message settings updated successfully',
            type: 'success',
          })
        );
      } catch (e) {
        dispatch(
          show({
            title: 'Failed to update message settings',
            description: formatServerError(e),
            type: 'error',
          })
        );
      }
    },
    [dispatch, updateMessageSettings]
  );

  if (isLoading) {
    return (
      <div className="flex h-[80vh] flex-col items-center justify-center">
        <LoadingIndicator />
      </div>
    );
  }

  // Should not happen because we've backfilled the settings, but just in case
  if (!messageSettings) {
    return (
      <div className="flex h-[80vh] flex-col items-center justify-center">
        <p>
          No message settings found, please contact support to create the
          missing settings.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full min-h-[25vh]">
      <FormProvider {...form}>
        <MessageSettingsForm />
      </FormProvider>
      <div className="fixed bottom-0 left-0 right-0 flex h-[64px] items-center justify-end border-t border-gray-900/10 bg-white px-4">
        <Button
          type="submit"
          variant="primary"
          disabled={form.formState.isSubmitting}
          onClick={form.handleSubmit(handleSubmit)}
        >
          {form.formState.isSubmitting
            ? 'Saving Message Settings...'
            : 'Save Message Settings'}
        </Button>
      </div>
    </div>
  );
}
